/**
 * One document, end to end — and deliberately not one *kind* of document.
 *
 * The screen shows what the parser actually found: how each page was read, the text it
 * produced, the label/value pairs and checkboxes it recovered, and the findings that
 * need a person. The claims lens appears only when a document produced loss rows, so an
 * invoice, an ID card and a loss run all get a console that fits them.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { download, get, post } from "../lib/api";
import type {
  ContentResponse,
  DocumentDetail as Detail,
  ExceptionList,
  RowList,
} from "../lib/types";
import { Sep, Shell } from "../components/Shell";
import { PageMap, PageView } from "../components/PageView";
import { NoPreview, StructurePreview } from "../components/Preview";
import { PageContentView } from "../components/PageContentView";
import { ClaimsLens } from "../components/ClaimsLens";
import { FlagsTable } from "../components/FlagsTable";
import {
  Button,
  Chip,
  Empty,
  Failed,
  Loading,
  METHOD_COLOR,
  Panel,
  Metric,
} from "../components/ui";
import { DocumentHeader } from "../components/DocumentHeader";
import { DocumentOrganize } from "../components/DocumentOrganize";

type Tab = "text" | "fields" | "pairs" | "claims" | "findings" | "json";

/** Split a search query into the terms worth locating: whitespace-separated, ≥2 chars,
 * longest first so the most distinctive term wins the highlight. */
export function queryTerms(q: string): string[] {
  return q
    .split(/\s+/)
    .filter((t) => t.length >= 2)
    .sort((a, b) => b.length - a.length);
}

/** The page that best matches the query, and the term to highlight on it. Ranks pages by
 * how many *distinct* query terms they contain (ties → earliest page), so a dense match on
 * the real answer page beats an early page that merely shares a common word like "find".
 * The needle prefers a distinctive term (one with a digit/symbol, e.g. "1/10") over a plain
 * word, and is returned in its actual page casing for the exact-match highlighter. */
export function locateMatch(
  pages: { markdown?: string | null }[],
  terms: string[],
): { index: number; needle: string } | null {
  let best: { index: number; score: number; needle: string } | null = null;
  for (let i = 0; i < pages.length; i++) {
    const md = pages[i]?.markdown ?? "";
    const lower = md.toLowerCase();
    let score = 0;
    let firstPresent: string | null = null;
    let distinctive: string | null = null;
    for (const term of terms) {
      const at = lower.indexOf(term.toLowerCase());
      if (at === -1) continue;
      score += 1;
      const occurrence = md.slice(at, at + term.length);
      if (firstPresent === null) firstPresent = occurrence;
      if (distinctive === null && /[^a-zA-Z]/.test(term)) distinctive = occurrence;
    }
    if (score > 0 && (best === null || score > best.score)) {
      best = { index: i, score, needle: distinctive ?? firstPresent! };
    }
  }
  return best && { index: best.index, needle: best.needle };
}

export function DocumentDetail() {
  const { id = "" } = useParams();
  const [tab, setTab] = useState<Tab | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);

  const queryClient = useQueryClient();

  const reparse = useMutation({
    mutationFn: () => post<unknown>(`/v1/documents/${id}/reparse`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document", id] });
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });

  const doc = useQuery({
    queryKey: ["document", id],
    queryFn: () => get<Detail>(`/v1/documents/${id}`),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "queued" || status === "processing" ? 2_000 : false;
    },
  });

  const parsed = doc.data?.status === "succeeded";
  // What kind of thing this document is, decided by the server from the parse's outcome.
  // Older servers do not send it; `rendered` keeps their behaviour exactly as it was.
  const mode = doc.data?.preview?.mode ?? "rendered";
  const runId = doc.data?.parse_run_id ?? null;

  const content = useQuery({
    queryKey: ["content", id],
    queryFn: () => get<ContentResponse>(`/v1/documents/${id}/content`),
    enabled: parsed,
  });
  const rows = useQuery({
    queryKey: ["rows", runId],
    queryFn: () => get<RowList>(`/v1/runs/${runId}/rows?limit=200`),
    enabled: parsed && runId !== null,
  });
  const exceptions = useQuery({
    queryKey: ["exceptions", id],
    queryFn: () => get<ExceptionList>(`/v1/documents/${id}/exceptions`),
    enabled: parsed,
  });

  const pages = useMemo(() => content.data?.content.pages ?? [], [content.data]);

  // Arriving from a search (`?q=…`): jump to the first page holding a query term and
  // highlight it, so a result lands where the match is instead of at page 1. The jump
  // fires once (a ref guard), so manual page navigation afterwards is never overridden.
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") ?? "";
  const located = useMemo(() => {
    const terms = queryTerms(searchQuery);
    return terms.length ? locateMatch(pages, terms) : null;
  }, [pages, searchQuery]);
  const jumped = useRef(false);
  useEffect(() => {
    if (located && !jumped.current) {
      jumped.current = true;
      setPageIndex(located.index);
    }
  }, [located]);
  const routes = useMemo(
    () =>
      pages.reduce<Record<string, number>>((tally, page) => {
        tally[page.method] = (tally[page.method] ?? 0) + 1;
        return tally;
      }, {}),
    [pages],
  );

  if (doc.isPending) {
    return (
      <Shell crumb={<span>documents</span>}>
        <Loading what="the document" />
      </Shell>
    );
  }
  if (doc.isError || !doc.data) {
    return (
      <Shell crumb={<span>documents</span>}>
        <Failed error={doc.error} what="the document" />
      </Shell>
    );
  }

  const document_ = doc.data;
  const page = pages[Math.min(pageIndex, Math.max(pages.length - 1, 0))];
  const claims = rows.data?.rows ?? [];
  const flags = exceptions.data?.exceptions ?? [];
  const fields = content.data?.fields ?? {};
  const pairs = content.data?.content.kv ?? [];
  const hasClaims = claims.length > 0;

  // The left panel shows the page image when there is one and the page text when there
  // is not, so the "text" tab earns its place only in the first case.
  const hasGeometry = (page?.regions?.length ?? 0) > 0;
  const hasFields = Object.keys(fields).length > 0;

  const tabs: [Tab, string, number | null][] = [
    ...(hasGeometry ? ([["text", "text", pages.length]] as [Tab, string, number][]) : []),
    ["fields", "fields", Object.keys(fields).length],
    ["pairs", "pairs", pairs.length],
    ...(hasClaims ? ([["claims", "claims", rows.data?.total ?? claims.length]] as [Tab, string, number][]) : []),
    ["findings", "findings", flags.length],
    ["json", "json", null],
  ];

  // Open on whatever wants attention, then on whatever the document produced — never on
  // a tab that is empty for this document.
  const region =
    selectedRegion === null ? undefined : (page?.regions ?? [])[selectedRegion];

  // Arriving from a search on the matched page: open the text tab so the highlighted
  // term is visible immediately, not behind a tab the reader has to find.
  const onSearchHit = located !== null && pageIndex === located.index;
  // The term to highlight on this page — for the text tab (geometry pages) and the
  // text-layer render (PageView) alike. Null unless we are on the matched page.
  const searchNeedle = located && onSearchHit ? located.needle : null;

  const fallback: Tab = region
    ? "text"
    : onSearchHit && hasGeometry
    ? "text"
    : flags.length
    ? "findings"
    : hasClaims
      ? "claims"
      : hasGeometry
        ? "text"
        : hasFields
          ? "fields"
          : "json";
  const available = tabs.map(([value]) => value);
  const activeTab: Tab = tab && available.includes(tab) ? tab : fallback;

  return (
    <Shell
      fill
      crumb={
        <>
          <Link to="/documents" className="transition-colors hover:text-gold">
            documents
          </Link>
          <Sep />
          <span className="truncate text-text normal-case">{document_.filename ?? "unnamed"}</span>
          <Sep />
          <span>{document_.id.slice(0, 8)}</span>
        </>
      }
    >
      <DocumentHeader
        document={document_}
        hasClaims={hasClaims}
        runId={runId}
        reparse={reparse}
        onExportWorkbook={() => void download(`/v1/runs/${runId}/workbook`, "claims.xlsx")}
      />
      <DocumentOrganize
        documentId={document_.id}
        tags={document_.tags}
        collections={document_.collections}
      />

      {document_.status === "stale" && (
        <Panel title="parsed, but nothing was stored">
          <div className="flex flex-col gap-3 px-4 py-4">
            <p className="max-w-[74ch] text-[13px] leading-relaxed text-dim">
              The job for this document finished and yet no parse run exists for it at any
              version. That is not an ordinary state — it usually means a worker claimed the
              job, decided the work was already done, and stored nothing. Reading it again is
              safe: parsing is idempotent.
            </p>
            <Button
              variant="primary"
              disabled={reparse.isPending}
              onClick={() => reparse.mutate()}
              className="self-start"
            >
              {reparse.isPending ? "queueing…" : `re-parse at ${document_.parser_version}`}
            </Button>
            {reparse.isError && (
              <p className="font-mono text-[11.5px] text-crit">
                &gt; {reparse.error instanceof Error ? reparse.error.message : "could not queue"}
              </p>
            )}
            {reparse.isSuccess && (
              <p className="font-mono text-[11.5px] text-ok">
                &gt; queued — this page follows the job as it runs
              </p>
            )}
          </div>
        </Panel>
      )}

      {/*
        Read by an older build. Deliberately a line, not a panel, and it does not gate the
        content below it: the parse succeeded and is shown. Re-parsing is an option the
        operator may take, not a toll they must pay to see what they already have.
      */}
      {document_.current === false && document_.run_parser_version && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border border-hair bg-ground-soft px-4 py-2.5">
          <span className="eyebrow text-faint">read by</span>
          <span className="font-mono text-[11.5px] text-dim">
            {document_.run_parser_version}
          </span>
          <span className="text-faint">·</span>
          <span className="text-[12.5px] text-dim">
            this server runs{" "}
            <span className="font-mono text-gold">{document_.parser_version}</span>
          </span>
          <button
            type="button"
            disabled={reparse.isPending}
            onClick={() => reparse.mutate()}
            className="ms-auto font-mono text-[11px] tracking-[0.14em] text-gold uppercase underline-offset-4 hover:underline disabled:opacity-50"
          >
            {reparse.isPending ? "queueing…" : "re-read at current"}
          </button>
          {reparse.isSuccess && (
            <span className="font-mono text-[11px] text-ok">&gt; queued</span>
          )}
        </div>
      )}

      {/* progress, while the read is unfinished */}
      {!parsed && document_.status !== "stale" && (
        <Panel title="reading">
          <div className="px-4 py-4">
            {document_.progress ? (
              <>
                <div className="flex items-baseline justify-between font-mono">
                  <p className="text-[12px] font-bold tracking-[0.16em] text-gold uppercase">
                    {document_.progress.stage}
                  </p>
                  <p className="tnum text-[12px] text-dim">
                    {document_.progress.pages_done ?? 0} / {document_.progress.pages_total ?? "?"} pages
                  </p>
                </div>
                <div className="mt-3 h-[3px] bg-hair">
                  <div
                    className="h-full bg-gold transition-[width] duration-500 ease-alt"
                    style={{
                      width: `${
                        document_.progress.pages_total
                          ? Math.min(
                              100,
                              ((document_.progress.pages_done ?? 0) / document_.progress.pages_total) * 100,
                            )
                          : 6
                      }%`,
                    }}
                  />
                </div>
              </>
            ) : (
              <p className="font-mono text-[12px] text-dim">
                &gt; queued — a worker picks this up on its next poll.
              </p>
            )}
            {document_.job && (
              <p className="mt-3 font-mono text-[10.5px] text-faint">
                job {document_.job.id.slice(0, 8)} · {document_.job.state} · attempt{" "}
                {document_.job.attempts}
              </p>
            )}
            {document_.job?.last_error && (
              <p className="mt-2 border border-crit/40 bg-crit-wash px-3 py-2 font-mono text-[11px] text-crit">
                {document_.job.last_error}
              </p>
            )}
          </div>
        </Panel>
      )}

      {parsed && content.isError && <Failed error={content.error} what="this document's content" />}

      {/* what came out */}
      {parsed && !content.isError && (
        <div className="hairgrid shrink-0 grid-cols-2 lg:grid-cols-4">
          <Metric label="pages" value={pages.length} note="all read" />
          <Metric
            label="route"
            value={
              Object.entries(routes)
                .map(([method, count]) => `${count} ${method.replace("_layer", "")}`)
                .join("  ") || "—"
            }
            note={
              <span className="inline-flex h-[3px] w-16 align-middle">
                {Object.entries(routes).map(([method, count]) => (
                  <i
                    key={method}
                    style={{
                      width: `${(count / Math.max(pages.length, 1)) * 100}%`,
                      background: METHOD_COLOR[method] ?? "var(--text-faint)",
                    }}
                  />
                ))}
              </span>
            }
          />
          <Metric
            label="fields"
            value={Object.keys(fields).length}
            note={hasFields ? "pairs + checkboxes" : "no form structure"}
          />
          <Metric
            label="findings"
            value={flags.length}
            valueClass={flags.length ? "text-crit" : "text-ok"}
            note={
              flags.length
                ? `${flags.filter((f) => f.severity === "high").length} high`
                : "nothing to answer for"
            }
          />
        </div>
      )}

      {/* the page, and what was pulled out of it */}
      {parsed && !content.isError && (
        <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
          <section className="flex min-h-0 flex-col overflow-hidden border border-hair bg-surface">
            {mode !== "rendered" && mode !== "none" ? (
              /*
               * A format with no printed page. Its structure is what the parse recovered —
               * cells with their real coordinates, slides with their notes, headings at
               * their real depth — and it is exact for these formats rather than inferred.
               * Until now this pane showed a 409 that read as a failure.
               */
              <StructurePreview
                documentId={document_.id}
                mode={mode}
                fallback={
                  page ? (
                    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                      <p className="mb-3 text-[12.5px] text-faint">
                        This document was parsed before its structure was kept, so only the
                        flattened text is available. Re-reading it recovers the rest.
                      </p>
                      <pre className="text-[12.5px] leading-relaxed whitespace-pre-wrap text-dim">
                        {page.markdown}
                      </pre>
                    </div>
                  ) : (
                    <Loading what="page content" />
                  )
                }
              />
            ) : mode === "none" ? (
              <NoPreview reason={document_.preview?.reason} />
            ) : content.isPending ? (
              <Loading what="page content" />
            ) : page ? (
              <>
                <PageView
                  page={page}
                  documentId={document_.id}
                  selected={selectedRegion}
                  onSelect={(index) => {
                    setSelectedRegion(index);
                    if (index !== null) setTab("text");
                  }}
                  needle={searchNeedle}
                />
                <div className="flex shrink-0 flex-wrap items-center gap-3 border-t border-hair bg-hair-soft px-4 py-2">
                  <Button
                    variant="ghost"
                    disabled={pageIndex === 0}
                    onClick={() => {
                      setSelectedRegion(null);
                      setPageIndex((i) => Math.max(0, i - 1));
                    }}
                  >
                    ←
                  </Button>
                  <span className="tnum font-mono text-[11px] text-dim">
                    {page.index + 1} / {pages.length}
                  </span>
                  <Button
                    variant="ghost"
                    disabled={pageIndex >= pages.length - 1}
                    onClick={() => {
                      setSelectedRegion(null);
                      setPageIndex((i) => Math.min(pages.length - 1, i + 1));
                    }}
                  >
                    →
                  </Button>
                  <div className="ms-auto">
                    <PageMap
                      pages={pages}
                      current={pageIndex}
                      onPick={(index) => {
                        setSelectedRegion(null);
                        setPageIndex(index);
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <Empty title="no page content" hint="This document produced no readable pages." />
            )}
          </section>

          <section className="flex min-h-0 flex-col overflow-hidden border border-hair bg-surface">
            <div className="flex shrink-0 flex-wrap border-b border-hair bg-hair-soft px-2">
              {tabs.map(([value, label, count]) => (
                <button
                  key={value}
                  role="tab"
                  aria-selected={activeTab === value}
                  onClick={() => setTab(value)}
                  className={`border-b-2 px-3 py-2.5 font-mono text-[10.5px] tracking-[0.16em] uppercase transition-colors duration-200 ease-alt ${
                    activeTab === value
                      ? "border-gold font-bold text-gold"
                      : "border-transparent text-dim hover:text-text"
                  }`}
                >
                  {label}
                  {count !== null && <span className="tnum ms-1.5 text-faint">{count}</span>}
                </button>
              ))}
            </div>

            {activeTab === "text" && (
              <PageContentView
                markdown={page?.markdown ?? ""}
                needle={region?.text ?? searchNeedle}
                className="min-h-0 flex-1 overflow-auto px-4 py-3"
              />
            )}

            {activeTab === "fields" && (
              <div className="min-h-0 flex-1 overflow-auto">
                {Object.keys(fields).length === 0 ? (
                  <Empty
                    title="no form fields"
                    hint="Nothing in this document read as a label with a value, and no checkboxes were detected. The page text is on the left."
                  />
                ) : (
                  Object.entries(fields).map(([key, value]) => (
                    <div
                      key={key}
                      className="grid grid-cols-[minmax(0,150px)_minmax(0,1fr)] gap-3 border-b border-hair-soft px-4 py-2"
                    >
                      <span className="truncate font-mono text-[10.5px] tracking-[0.06em] text-faint">
                        {key}
                      </span>
                      <span className="text-[13px] break-words">
                        {typeof value === "boolean" ? (
                          <Chip tone={value ? "ok" : "mut"}>{value ? "checked" : "unchecked"}</Chip>
                        ) : Array.isArray(value) ? (
                          value.join(" · ")
                        ) : (
                          String(value)
                        )}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === "pairs" && (
              <div className="min-h-0 flex-1 overflow-auto">
                {pairs.length === 0 ? (
                  <Empty
                    title="no label pairs"
                    hint="A pair is only reported when the layout makes it unambiguous — a guessed pair is worse than none."
                  />
                ) : (
                  pairs.map((pair, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-[minmax(0,150px)_minmax(0,1fr)] gap-3 border-b border-hair-soft px-4 py-2"
                    >
                      <span className="truncate font-mono text-[10.5px] tracking-[0.06em] text-faint">
                        {pair.key}
                      </span>
                      <span className="text-[13px] break-words">{pair.value}</span>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === "claims" && hasClaims && (
              <div className="min-h-0 flex-1 overflow-auto">
                <ClaimsLens rows={claims} />
              </div>
            )}

            {activeTab === "findings" &&
              (exceptions.isPending ? (
                <Loading what="findings" />
              ) : exceptions.isError ? (
                <Failed error={exceptions.error} what="findings" />
              ) : (
                <div className="min-h-0 flex-1 overflow-auto">
                  <FlagsTable flags={flags} />
                </div>
              ))}

            {activeTab === "json" && (
              <pre className="min-h-0 flex-1 overflow-auto bg-ground-soft px-4 py-3 font-mono text-[11px] leading-relaxed whitespace-pre text-dim">
                {JSON.stringify(hasClaims ? claims[0] : fields, null, 2)}
              </pre>
            )}
          </section>
        </div>
      )}

    </Shell>
  );
}
